import React, { useRef, useState, useCallback, useEffect } from 'react';
import { TransformWrapper, TransformComponent, ReactZoomPanPinchRef } from 'react-zoom-pan-pinch';
import { Stage, Layer, Rect, Text, Line, Arrow } from 'react-konva';
import { 
  Plus, 
  Square, 
  Type, 
  Download,
  Upload,
  Minus,
  Home,
  Hand,
  MousePointer
} from 'lucide-react';

interface CanvasNode {
  id: string;
  type: 'note' | 'image' | 'shape' | 'text';
  x: number;
  y: number;
  width: number;
  height: number;
  content: string;
  color: string;
  borderColor?: string;
  fontSize?: number;
  fontFamily?: string;
  rotation?: number;
}

interface CanvasConnection {
  id: string;
  from: string;
  to: string;
  points: number[];
  color: string;
  strokeWidth: number;
  label?: string;
}

interface CanvasState {
  nodes: CanvasNode[];
  connections: CanvasConnection[];
  selectedNodeId: string | null;
  selectedConnectionId: string | null;
}

interface CanvasViewProps {
  content?: {
    nodes?: CanvasNode[];
    connections?: CanvasConnection[];
  };
  onContentChange?: (content: any) => void;
}

const CanvasView: React.FC<CanvasViewProps> = ({ content, onContentChange }) => {
  const stageRef = useRef<any>(null);
  const transformRef = useRef<ReactZoomPanPinchRef>(null);
  
  const [canvasState, setCanvasState] = useState<CanvasState>({
    nodes: content?.nodes || [],
    connections: content?.connections || [],
    selectedNodeId: null,
    selectedConnectionId: null
  });
  
  const [tool, setTool] = useState<'select' | 'pan' | 'note' | 'shape' | 'text' | 'connect'>('select');
  const [isDrawingConnection, setIsDrawingConnection] = useState(false);
  const [connectionStart, setConnectionStart] = useState<string | null>(null);
  const [tempConnection, setTempConnection] = useState<number[] | null>(null);

  // Sync with parent content changes
  useEffect(() => {
    if (content) {
      setCanvasState(prev => ({
        ...prev,
        nodes: content.nodes || prev.nodes,
        connections: content.connections || prev.connections
      }));
    }
  }, [content]);

  // Notify parent of changes
  const notifyContentChange = useCallback((newState: CanvasState) => {
    if (onContentChange) {
      onContentChange({
        nodes: newState.nodes,
        connections: newState.connections
      });
    }
  }, [onContentChange]);

  const addNode = useCallback((type: CanvasNode['type'], x = 100, y = 100) => {
    const newNode: CanvasNode = {
      id: `node-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type,
      x,
      y,
      width: type === 'note' ? 200 : type === 'text' ? 100 : 120,
      height: type === 'note' ? 150 : type === 'text' ? 40 : 80,
      content: type === 'note' ? 'New Note\n\nClick to edit...' : type === 'text' ? 'Text' : type === 'shape' ? '' : 'New Item',
      color: type === 'note' ? '#75b2b8' : type === 'text' ? 'transparent' : '#303a46',
      borderColor: type === 'text' ? '#75b2b8' : undefined,
      fontSize: type === 'text' ? 16 : 14,
      fontFamily: 'Inter, sans-serif',
      rotation: 0
    };
    
    setCanvasState(prev => {
      const newState = { ...prev, nodes: [...prev.nodes, newNode] };
      notifyContentChange(newState);
      return newState;
    });
  }, [notifyContentChange]);

  const updateNode = useCallback((nodeId: string, updates: Partial<CanvasNode>) => {
    setCanvasState(prev => {
      const newState = {
        ...prev,
        nodes: prev.nodes.map(node => 
          node.id === nodeId ? { ...node, ...updates } : node
        )
      };
      notifyContentChange(newState);
      return newState;
    });
  }, [notifyContentChange]);

  const deleteNode = useCallback((nodeId: string) => {
    setCanvasState(prev => {
      const newState = {
        ...prev,
        nodes: prev.nodes.filter(node => node.id !== nodeId),
        connections: prev.connections.filter(conn => conn.from !== nodeId && conn.to !== nodeId),
        selectedNodeId: prev.selectedNodeId === nodeId ? null : prev.selectedNodeId
      };
      notifyContentChange(newState);
      return newState;
    });
  }, [notifyContentChange]);

  const addConnection = useCallback((fromId: string, toId: string) => {
    const fromNode = canvasState.nodes.find(n => n.id === fromId);
    const toNode = canvasState.nodes.find(n => n.id === toId);
    
    if (!fromNode || !toNode || fromId === toId) return;

    const newConnection: CanvasConnection = {
      id: `conn-${Date.now()}`,
      from: fromId,
      to: toId,
      points: [
        fromNode.x + fromNode.width / 2,
        fromNode.y + fromNode.height / 2,
        toNode.x + toNode.width / 2,
        toNode.y + toNode.height / 2
      ],
      color: '#75b2b8',
      strokeWidth: 2
    };

    setCanvasState(prev => {
      const newState = { ...prev, connections: [...prev.connections, newConnection] };
      notifyContentChange(newState);
      return newState;
    });
  }, [canvasState.nodes, notifyContentChange]);

  const updateConnection = useCallback((nodeId: string) => {
    setCanvasState(prev => {
      const node = prev.nodes.find(n => n.id === nodeId);
      if (!node) return prev;

      const newState = {
        ...prev,
        connections: prev.connections.map(conn => {
          if (conn.from === nodeId || conn.to === nodeId) {
            const fromNode = prev.nodes.find(n => n.id === conn.from);
            const toNode = prev.nodes.find(n => n.id === conn.to);
            
            if (fromNode && toNode) {
              return {
                ...conn,
                points: [
                  fromNode.x + fromNode.width / 2,
                  fromNode.y + fromNode.height / 2,
                  toNode.x + toNode.width / 2,
                  toNode.y + toNode.height / 2
                ]
              };
            }
          }
          return conn;
        })
      };
      notifyContentChange(newState);
      return newState;
    });
  }, [notifyContentChange]);

  const exportCanvas = useCallback(() => {
    const canvasData = {
      ...canvasState,
      timestamp: new Date().toISOString(),
      version: '1.0'
    };
    
    const blob = new Blob([JSON.stringify(canvasData, null, 2)], {
      type: 'application/json'
    });
    
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `canvas-${Date.now()}.json`;
    link.click();
    
    URL.revokeObjectURL(url);
  }, [canvasState]);

  const importCanvas = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target?.result as string);
        if (data.nodes && data.connections) {
          const newState = {
            nodes: data.nodes,
            connections: data.connections,
            selectedNodeId: null,
            selectedConnectionId: null
          };
          setCanvasState(newState);
          notifyContentChange(newState);
        }
      } catch (error) {
        console.error('Failed to import canvas:', error);
      }
    };
    reader.readAsText(file);
  }, [notifyContentChange]);

  const resetView = useCallback(() => {
    if (transformRef.current) {
      transformRef.current.resetTransform();
    }
  }, []);

  const zoomIn = useCallback(() => {
    if (transformRef.current) {
      transformRef.current.zoomIn(0.2);
    }
  }, []);

  const zoomOut = useCallback(() => {
    if (transformRef.current) {
      transformRef.current.zoomOut(0.2);
    }
  }, []);

  const handleStageClick = useCallback((e: any) => {
    // Clicking on stage background
    if (e.target === e.target.getStage()) {
      if (tool === 'note') {
        const pos = e.target.getStage().getPointerPosition();
        addNode('note', pos.x - 100, pos.y - 75);
        setTool('select');
      } else if (tool === 'shape') {
        const pos = e.target.getStage().getPointerPosition();
        addNode('shape', pos.x - 60, pos.y - 40);
        setTool('select');
      } else if (tool === 'text') {
        const pos = e.target.getStage().getPointerPosition();
        addNode('text', pos.x - 50, pos.y - 20);
        setTool('select');
      } else {
        setCanvasState(prev => ({ ...prev, selectedNodeId: null }));
      }
      
      setIsDrawingConnection(false);
      setConnectionStart(null);
      setTempConnection(null);
    }
  }, [tool, addNode]);

  const handleNodeClick = useCallback((nodeId: string, e: any) => {
    e.cancelBubble = true;
    
    if (tool === 'connect' || isDrawingConnection) {
      if (!connectionStart) {
        setConnectionStart(nodeId);
        setIsDrawingConnection(true);
      } else if (connectionStart !== nodeId) {
        addConnection(connectionStart, nodeId);
        setIsDrawingConnection(false);
        setConnectionStart(null);
        setTool('select');
      }
    } else {
      setCanvasState(prev => ({ ...prev, selectedNodeId: nodeId }));
    }
  }, [tool, isDrawingConnection, connectionStart, addConnection]);

  const handleNodeDragEnd = useCallback((nodeId: string, e: any) => {
    const newPos = { x: e.target.x(), y: e.target.y() };
    updateNode(nodeId, newPos);
    updateConnection(nodeId);
  }, [updateNode, updateConnection]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Delete' || e.key === 'Backspace') {
      if (canvasState.selectedNodeId) {
        deleteNode(canvasState.selectedNodeId);
      }
    } else if (e.key === 'Escape') {
      setCanvasState(prev => ({ ...prev, selectedNodeId: null }));
      setIsDrawingConnection(false);
      setConnectionStart(null);
      setTool('select');
    }
  }, [canvasState.selectedNodeId, deleteNode]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return (
    <div className="obsidian-canvas-view">
      {/* Canvas Toolbar */}
      <div className="canvas-toolbar">
        <div className="canvas-toolbar-group">
          <button
            onClick={() => setTool(tool === 'select' ? 'select' : 'select')}
            className={`canvas-tool-btn ${tool === 'select' ? 'active' : ''}`}
            title="Select Tool (V)"
          >
            <MousePointer size={16} />
          </button>
          
          <button
            onClick={() => setTool('pan')}
            className={`canvas-tool-btn ${tool === 'pan' ? 'active' : ''}`}
            title="Pan Tool (H)"
          >
            <Hand size={16} />
          </button>
          
          <div className="canvas-toolbar-divider" />
          
          <button
            onClick={() => setTool('note')}
            className={`canvas-tool-btn ${tool === 'note' ? 'active' : ''}`}
            title="Add Note"
          >
            <Plus size={16} />
            Note
          </button>
          
          <button
            onClick={() => setTool('shape')}
            className={`canvas-tool-btn ${tool === 'shape' ? 'active' : ''}`}
            title="Add Shape"
          >
            <Square size={16} />
            Shape
          </button>
          
          <button
            onClick={() => setTool('text')}
            className={`canvas-tool-btn ${tool === 'text' ? 'active' : ''}`}
            title="Add Text"
          >
            <Type size={16} />
            Text
          </button>
          
          <button
            onClick={() => setTool('connect')}
            className={`canvas-tool-btn ${tool === 'connect' ? 'active' : ''}`}
            title="Connect Nodes"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M2 8a1 1 0 1 1 2 0 1 1 0 0 1-2 0zm10 0a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM4.5 8L11.5 8" stroke="currentColor" strokeWidth="1.5" fill="none"/>
            </svg>
            Connect
          </button>
        </div>
        
        <div className="canvas-toolbar-group">
          <button
            onClick={zoomOut}
            className="canvas-tool-btn"
            title="Zoom Out (-)"
          >
            <Minus size={16} />
          </button>
          
          <button
            onClick={zoomIn}
            className="canvas-tool-btn"
            title="Zoom In (+)"
          >
            <Plus size={16} />
          </button>
          
          <button
            onClick={resetView}
            className="canvas-tool-btn"
            title="Reset View (0)"
          >
            <Home size={16} />
          </button>
        </div>
        
        <div className="canvas-toolbar-group">
          <button
            onClick={exportCanvas}
            className="canvas-tool-btn"
            title="Export Canvas"
          >
            <Download size={16} />
          </button>
          
          <label className="canvas-tool-btn" title="Import Canvas">
            <Upload size={16} />
            <input
              type="file"
              accept=".json"
              onChange={importCanvas}
              style={{ display: 'none' }}
              aria-label="Import canvas file"
            />
          </label>
        </div>
      </div>

      {/* Infinite Canvas */}
      <div className="canvas-container">
        <TransformWrapper
          ref={transformRef}
          initialScale={1}
          minScale={0.1}
          maxScale={3}
          wheel={{ step: 0.1 }}
          panning={{ disabled: tool !== 'pan' && tool !== 'select' }}
          doubleClick={{ disabled: true }}
        >
          <TransformComponent
            wrapperStyle={{
              width: '100%',
              height: '100%',
              cursor: tool === 'pan' ? 'grab' : tool === 'select' ? 'default' : 'crosshair'
            }}
          >
            <Stage
              ref={stageRef}
              width={3000}
              height={3000}
              onMouseDown={handleStageClick}
              draggable={false}
            >
              {/* Grid Background */}
              <Layer>
                {Array.from({ length: 60 }, (_, i) => (
                  <Line
                    key={`grid-v-${i}`}
                    points={[i * 50, 0, i * 50, 3000]}
                    stroke="rgba(117, 178, 184, 0.05)"
                    strokeWidth={1}
                  />
                ))}
                {Array.from({ length: 60 }, (_, i) => (
                  <Line
                    key={`grid-h-${i}`}
                    points={[0, i * 50, 3000, i * 50]}
                    stroke="rgba(117, 178, 184, 0.05)"
                    strokeWidth={1}
                  />
                ))}
              </Layer>

              {/* Connections Layer */}
              <Layer>
                {canvasState.connections.map(connection => (
                  <Arrow
                    key={connection.id}
                    points={connection.points}
                    stroke={connection.color}
                    strokeWidth={connection.strokeWidth}
                    fill={connection.color}
                    pointerLength={8}
                    pointerWidth={8}
                  />
                ))}
              </Layer>

              {/* Nodes Layer */}
              <Layer>
                {canvasState.nodes.map(node => (
                  <React.Fragment key={node.id}>
                    <Rect
                      x={node.x}
                      y={node.y}
                      width={node.width}
                      height={node.height}
                      fill={node.color}
                      stroke={canvasState.selectedNodeId === node.id ? '#ffffff' : (node.borderColor || 'transparent')}
                      strokeWidth={2}
                      cornerRadius={node.type === 'shape' ? 4 : 8}
                      draggable={tool === 'select'}
                      onClick={(e) => handleNodeClick(node.id, e)}
                      onDragEnd={(e) => handleNodeDragEnd(node.id, e)}
                      shadowColor="rgba(0,0,0,0.3)"
                      shadowBlur={4}
                      shadowOffset={{ x: 2, y: 2 }}
                    />
                    <Text
                      x={node.x + 8}
                      y={node.y + 8}
                      text={node.content}
                      fontSize={node.fontSize || 14}
                      fontFamily={node.fontFamily || 'Inter, sans-serif'}
                      fill={node.type === 'text' ? '#e3efe3' : '#ffffff'}
                      width={node.width - 16}
                      height={node.height - 16}
                      verticalAlign="top"
                      wrap="word"
                      draggable={tool === 'select'}
                      onClick={(e) => handleNodeClick(node.id, e)}
                      onDragEnd={(e) => handleNodeDragEnd(node.id, e)}
                    />
                  </React.Fragment>
                ))}
              </Layer>

              {/* Temporary connection while drawing */}
              {tempConnection && (
                <Layer>
                  <Line
                    points={tempConnection}
                    stroke="#75b2b8"
                    strokeWidth={2}
                    dash={[5, 5]}
                  />
                </Layer>
              )}
            </Stage>
          </TransformComponent>
        </TransformWrapper>
      </div>

      {/* Status bar */}
      <div className="canvas-status-bar">
        <div className="canvas-status-left">
          {tool === 'connect' && 'Click two nodes to connect them'}
          {tool === 'note' && 'Click to add a note'}
          {tool === 'shape' && 'Click to add a shape'}
          {tool === 'text' && 'Click to add text'}
          {tool === 'select' && `${canvasState.nodes.length} nodes, ${canvasState.connections.length} connections`}
        </div>
        <div className="canvas-status-right">
          Press <kbd>Del</kbd> to delete • <kbd>Esc</kbd> to deselect
        </div>
      </div>
    </div>
  );
};

export default CanvasView;
